import { IFloorPlanModel } from '@api/models';
import { getAvailableCameraStorageList } from '@api/services/camera.service';
import FileUploadPreview from '@components/FileUploadPreview/FileUploadPreview';
import DragAndDropFileUpload from '@components/FormFields/DragAndDropFileUpload';
import { Button, Card, CircularProgress } from '@hyperflake/react-ui-library';
import { FormHelperText, Label } from '@library/forms';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { timezoneList } from 'modules/shared/helpers/shared.constants';
import { FloorPlanStatusLabel } from 'modules/site-walk/constant/floorPlan.utils';
import useUpdateFloorPlanForm from 'modules/site-walk/hooks/useUpdateFloorPlanForm';
import useUpdateFloorPlanImageForm from 'modules/site-walk/hooks/useUpdateFloorPlanImageForm';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ImagePreview from '../ImagePreview/ImagePreview';

interface IEditFloorPlanOptions {
    floorPlan: IFloorPlanModel;
}

const EditFloorPlan = (props: IEditFloorPlanOptions) => {
    const { floorPlan } = props;

    const { clientId, projectId } = useParams();

    const updateFloorPlanForm = useUpdateFloorPlanForm({ clientId, projectId, floorPlan });

    const updateFloorPlanImageForm = useUpdateFloorPlanImageForm({ clientId, projectId, floorPlan });

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(
        ['clients', clientId, 'projects', projectId, 'cameras', 'storage_names'],
        async () => {
            const data = getAvailableCameraStorageList(clientId, projectId);
            return data;
        }
    );

    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [
            { label: 'Select Storage', value: '' },
            ...storages?.filter((val) => val !== 'AWS-us-west-2')?.map((val) => ({ label: val, value: val })),
        ];
    }, [storages]);

    const handleSubmit = async (
        payload: Pick<IFloorPlanModel, 'name' | 'folder' | 'location' | 'status' | 'timezone' | 'storage' | 'imageName'>
    ) => {
        try {
            await updateFloorPlanForm.submit(payload);

            toast.success('Floor Plan Updated successfully.');
        } catch (ex: any) {
            toast.error(ex?.data?.message || 'Some error occured, please try again.');
            console.log(ex);
        }
    };

    const handleSubmitImage = async (payload: Pick<IFloorPlanModel, 'file'>) => {
        try {
            await updateFloorPlanImageForm.submit(payload);

            toast.success('Floor Plan Image Updated successfully.');
        } catch (ex: any) {
            toast.error(ex?.data?.message || 'Some error occured, please try again.');
            console.log(ex);
        }
    };

    return (
        <div>
            <Formik
                initialValues={updateFloorPlanForm.initialValues}
                validationSchema={updateFloorPlanForm.schema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue }) => (
                    <Form id="updateFloorPlan">
                        <Card className="mt-4">
                            <Card.Header>
                                <h6>Basic Details</h6>
                            </Card.Header>

                            <Card.Body>
                                <div className=" grid grid-cols-1  md:grid-cols-2">
                                    {/* Name */}
                                    <div className="col-span-1 md:col-span-2">
                                        <Label htmlFor="name">Name</Label>
                                        <FormikInput name="name" placeholder="Enter text here" />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="name" />
                                        </FormHelperText>
                                    </div>

                                    {/* Folder */}
                                    <div className="col-span-1 md:col-span-2">
                                        <Label htmlFor="folder">Folder</Label>
                                        <FormikInput name="folder" placeholder="Enter text here" />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="folder" />
                                        </FormHelperText>
                                    </div>

                                    {/* Storage */}
                                    <div className="col-span-1 md:col-span-2">
                                        <Label>Storage</Label>
                                        <div className="relative w-full">
                                            <FormikSelect
                                                name="storage"
                                                options={
                                                    storageIsLoading
                                                        ? [{ label: 'Loading...', value: '' }]
                                                        : storageOption || []
                                                }
                                                disabled={storageIsLoading}
                                            />
                                            {storageIsLoading && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                                                    <CircularProgress size={16} />
                                                </div>
                                            )}
                                        </div>
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="storage" />
                                        </FormHelperText>
                                    </div>

                                    {/* Latitude */}

                                    <div className="col-span-1 md:mr-2">
                                        <Label>Latitude</Label>
                                        <FormikInput
                                            type="text"
                                            name="location.latitude"
                                            placeholder="Enter text here"
                                        />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="location.latitude" />
                                        </FormHelperText>
                                    </div>

                                    <div className="col-span-1 md:ml-2">
                                        <Label>Longitude</Label>
                                        <FormikInput
                                            type="text"
                                            name="location.longitude"
                                            placeholder="Enter text here"
                                        />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="location.longitude" />
                                        </FormHelperText>
                                    </div>
                                    {/* Timezone */}
                                    <div className="col-span-1 md:col-span-2">
                                        <Label>Timezone</Label>
                                        <FormikSelect name="timezone" options={timezoneList} />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="timezone" />
                                        </FormHelperText>
                                    </div>

                                    {/* Status */}
                                    <div className="col-span-1 md:col-span-2 mt-4">
                                        <Label>Status</Label>
                                        <FormikSelect name="status" options={FloorPlanStatusLabel} />
                                        <FormHelperText variant="error">
                                            <ErrorMessage name="status" />
                                        </FormHelperText>
                                    </div>

                                    {/* Submit */}
                                    <div className="col-span-1 md:col-span-2">
                                        <Button
                                            type="submit"
                                            form="updateFloorPlan"
                                            size="lg"
                                            className="mt-4 w-full sm:w-32"
                                            disabled={updateFloorPlanForm.isSubmitting}
                                        >
                                            {updateFloorPlanForm.isSubmitting ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                'Submit'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Form>
                )}
            </Formik>

            <Formik
                initialValues={updateFloorPlanImageForm.initialValues}
                validationSchema={updateFloorPlanImageForm.schema}
                onSubmit={handleSubmitImage}
            >
                {({ values, setFieldValue }) => (
                    <Form id="updateFloorPlanImage">
                        <Card className="mt-4">
                            <Card.Header>
                                <h6>Update Image</h6>
                            </Card.Header>
                            <Card.Body>
                                <div className="grid grid-cols-12 gap-3 mt-2">
                                    <div className="col-span-12">
                                        <DragAndDropFileUpload
                                            text="Browse JPG/PNG/MP4"
                                            accept={{
                                                'image/*': ['.jpeg', '.jpg', '.png'],
                                                'video/*': [],
                                            }}
                                            onChange={(files) => setFieldValue('file', files[0])}
                                        />
                                    </div>

                                    <div className="col-span-1 md:col-span-2">
                                        {values.file && (
                                            <div>
                                                <FileUploadPreview
                                                    name={values.file.name}
                                                    size={values.file.size}
                                                    onCancel={() => setFieldValue('file', null)}
                                                />
                                            </div>
                                        )}
                                        {!values.file && values.imageName && values.imageUrl && (
                                            <div>
                                                <ImagePreview
                                                    src={values.imageUrl}
                                                    name={values.imageName}
                                                    // onCancel={() => {
                                                    //     setFieldValue('file', null);
                                                    //     setFieldValue('imageName', null);
                                                    //     setFieldValue('imageUrl', null);
                                                    // }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    form="updateFloorPlanImage"
                                    size={'lg'}
                                    className="mt-8 w-28"
                                    disabled={updateFloorPlanImageForm.isSubmitting}
                                >
                                    {updateFloorPlanImageForm.isSubmitting ? (
                                        <CircularProgress size={20} color={'inherit'} />
                                    ) : (
                                        'Submit'
                                    )}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EditFloorPlan;
